import type { AppRouter } from "@repo/api/types";
import { QueryClient } from "@tanstack/react-query";
import { TRPCClientError } from "@trpc/client";
import { isPortfolioHost } from "./portfolio-host";

/**
 * Check if a tRPC error is a client error (4xx equivalent).
 * These are deterministic — retrying won't help and they represent
 * expected business rules, not system failures.
 *
 * Examples: BAD_REQUEST (last owner can't leave), CONFLICT (duplicate slug),
 * FORBIDDEN, NOT_FOUND, UNAUTHORIZED, etc.
 */
function isClientError(error: unknown): boolean {
  if (!(error instanceof TRPCClientError)) return false;
  const trpcError = error as TRPCClientError<AppRouter>;
  const httpStatus = trpcError.data?.httpStatus;
  return (
    typeof httpStatus === "number" && httpStatus >= 400 && httpStatus < 500
  );
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data remains fresh for 2 minutes - prevents redundant API calls during
      // typical user sessions while ensuring data updates within reasonable time
      staleTime: 2 * 60 * 1000,
      // Garbage collection after 5 minutes - balances memory usage with instant
      // data availability when navigating back to recently viewed pages
      gcTime: 5 * 60 * 1000,
      // Retry strategy: 3 attempts with exponential backoff (1s, 2s, 4s) capped at 30s
      // Handles transient network issues without overwhelming the server
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Disabled globally to prevent a burst of refetches on every tab switch,
      // which can cascade into error boundaries and trigger page reloads.
      // Queries that need focus-based freshness (e.g., session) override this.
      refetchOnWindowFocus: false,
      // Always refetch after network reconnection - prevents stale data after
      // connectivity issues (overrides staleTime check)
      refetchOnReconnect: "always",
    },
    mutations: {
      // Retry once for transient failures (network blips), but not for
      // deterministic client errors (4xx) like BAD_REQUEST, CONFLICT, etc.
      retry: (failureCount, error) => {
        if (failureCount >= 1) return false;
        return !isClientError(error);
      },
      retryDelay: 1000,
      onError: (error) => {
        if (error instanceof TRPCClientError) {
          const trpcError = error as TRPCClientError<AppRouter>;
          // Redirect to login on auth errors — never from the marketing-domain
          // portfolio host, which has no login (EPD-10104).
          if (trpcError.data?.code === "UNAUTHORIZED" && !isPortfolioHost()) {
            window.location.href = "/login";
            return;
          }
          // Client errors (4xx) are expected business rule violations
          // (e.g., "last owner cannot leave", "slug already exists").
          // Log as warning so Datadog RUM doesn't track them as errors.
          if (isClientError(error)) {
            console.warn("Mutation rejected:", trpcError.message);
            return;
          }
        }
        console.error("Mutation error:", error);
      },
    },
  },
});
